import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

/**
 * Platform feature flags — global kill-switches the superadmin flips from
 * /admin/features to hide half-baked features pre-launch. Loaded once at
 * shell boot and cached for the session; a 30s server-side cache means a
 * toggle takes effect within ~30s (or next full reload).
 *
 * Pair with server-side enforcement in apps/api/src/plugins/feature-gate.ts
 * — the web hides nav/routes; the API 404s direct requests. Both are needed.
 */

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export type FeatureKey = 'intake_forms' | 'sso' | 'api_keys' | 'webhooks';
export type FeatureMap = Record<FeatureKey, boolean>;

const DEFAULT_FEATURES: FeatureMap = {
  intake_forms: false,
  sso: false,
  api_keys: false,
  webhooks: false,
};

interface FeatureContextValue {
  features: FeatureMap;
  /** False until the initial fetch resolves (success or failure). Consumers can
   * render skeletons or simply treat all features as disabled during load. */
  loaded: boolean;
  /** Force a re-fetch, e.g. after the admin toggles a flag. */
  refresh: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextValue>({
  features: DEFAULT_FEATURES,
  loaded: false,
  refresh: async () => {},
});

async function fetchFeatures(): Promise<FeatureMap> {
  try {
    const res = await fetch(`${API}/api/v1/platform/features`);
    if (!res.ok) return DEFAULT_FEATURES;
    const data = (await res.json()) as { features: Partial<FeatureMap> };
    return { ...DEFAULT_FEATURES, ...data.features };
  } catch {
    return DEFAULT_FEATURES;
  }
}

/**
 * Wraps the app shell so any component can call useFeatures(). Loads once
 * on mount; safe to mount above auth (the endpoint is public).
 */
export function FeatureProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<FeatureMap>(DEFAULT_FEATURES);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    const f = await fetchFeatures();
    setFeatures(f);
    setLoaded(true);
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <FeatureContext.Provider value={{ features, loaded, refresh }}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures(): FeatureContextValue {
  return useContext(FeatureContext);
}

/** Convenience helper for single-flag checks in components. */
export function useFeature(key: FeatureKey): boolean {
  return useFeatures().features[key];
}
